import { createOne, deleteOne, fetchAllPages, fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { runWorkflowAction, type WorkflowResult } from '@/shared/api/workflow';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import type { CrudSchema } from '@/shared/crud/schemaCrud';
import { boolCol, dateCol, refCol } from '@/shared/crud/schemaCrud.utils';

/**
 * Travel (trasferte) — modello HR enterprise v2. Risorsa NUOVA e dedicata `travel-bookings`
 * con workflow proprio, INDIPENDENTE dai booking legacy `/api/bookings` (VehicleBooking).
 * Contratto verificato via be-contract sul BE reale.
 *
 * Divergenze/limiti noti:
 *  - envelope azioni SENZA `meta.transitionedTo` → stato nuovo da `data.attributes.statusCategory`.
 *  - create → stato `requested` (nessun submit separato).
 *  - stati `active`/`expired` NON raggiungibili (nessun setter) → non esporli come azioni.
 *  - `travel-policies` = solo dati consultivi, tetti NON enforced dal BE.
 *  - NESSUNA relationship media-files (biglietti/ricevute non allegabili).
 *  - NO feature-flag runtime (`travel.enabled` non controllato).
 */

export const TRAVEL_STATUSES = ['requested', 'pm_approved', 'pm_rejected', 'quoted', 'confirmed', 'active', 'completed', 'cancelled', 'expired'] as const;
export type TravelStatus = (typeof TRAVEL_STATUSES)[number];

export const STATUS_LABEL: Record<TravelStatus, string> = {
  requested: 'Richiesta', pm_approved: 'Approvata PM', pm_rejected: 'Respinta PM', quoted: 'Preventivata',
  confirmed: 'Confermata', active: 'In corso', completed: 'Conclusa', cancelled: 'Annullata', expired: 'Scaduta',
};
// Palette MaeDesign: azzurro (attesa/in corso) · green (ok) · red (ko) · gray (neutro) · yellow (avviso).
export const STATUS_COLOR: Record<TravelStatus, string> = {
  requested: 'yellow', pm_approved: 'azzurro', pm_rejected: 'red', quoted: 'azzurro',
  confirmed: 'green', active: 'green', completed: 'gray', cancelled: 'gray', expired: 'gray',
};

export const TRAVEL_TYPES = ['hotel', 'train', 'flight', 'ferry', 'car-rental-short', 'transfer', 'parking', 'other'] as const;
export const TRAVEL_TYPE_LABEL: Record<string, string> = {
  hotel: 'Hotel', train: 'Treno', flight: 'Aereo', ferry: 'Traghetto', 'car-rental-short': 'Noleggio auto', transfer: 'Transfer', parking: 'Parcheggio', other: 'Altro',
};
export const PAYER_TYPES = ['company-prepaid', 'employee-advance', 'client-billable'] as const;
export const PAYER_LABEL: Record<string, string> = { 'company-prepaid': 'Prepagato azienda', 'employee-advance': 'Anticipo dipendente', 'client-billable': 'A carico cliente' };
export const PRIORITIES = ['normal', 'urgent'] as const;
export const PRIORITY_LABEL: Record<string, string> = { normal: 'Normale', urgent: 'Urgente' };
export const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'] as const;

export type TravelAction =
  | 'pm-approve' | 'pm-reject' | 'quote' | 'confirm-single' | 'cancel' | 'mark-completed'
  | 'remind-approver' | 'bypass-approval';
export const ACTION_LABEL: Record<TravelAction, string> = {
  'pm-approve': 'Approva', 'pm-reject': 'Respingi', quote: 'Passa a preventivi', 'confirm-single': 'Conferma diretta', cancel: 'Annulla', 'mark-completed': 'Concludi',
  'remind-approver': 'Sollecita il responsabile', 'bypass-approval': 'Approva al posto del responsabile',
};

/**
 * Azioni ammesse per stato + ruolo.
 *
 * Dal BE v1.92.0 il gate di `pm-approve` accoglie anche il responsabile diretto e i suoi
 * delegati, e **respinge il richiedente stesso** anche col claim org-wide: nessuno è
 * approvatore di sé, che è la stessa garanzia che la coda già dava.
 */
export function allowedTravelActions(
  status: TravelStatus,
  opts: { isApprover: boolean; isAssigner: boolean; isOwner: boolean; canBypass?: boolean },
): TravelAction[] {
  const acts: TravelAction[] = [];
  /* Annulla il richiedente, o chi assegna (`travel.booking.assign.write`, superadmin, HR): è il gate
     di `POST /cancel` sul server. **Non l'approvatore**: fino al 26-10-2026 lo vedeva anche lui, e
     il server gli rispondeva 403. Chi approva e non vuole la trasferta la respinge. */
  const canCancel = opts.isOwner || opts.isAssigner;
  if (status === 'requested') {
    if (opts.isApprover && !opts.isOwner) acts.push('pm-approve', 'pm-reject');
    if (opts.isOwner || opts.isAssigner) acts.push('remind-approver');
    if (opts.canBypass && !opts.isOwner) acts.push('bypass-approval');
    if (canCancel) acts.push('cancel');
  } else if (status === 'pm_approved') {
    if (opts.isAssigner) acts.push('quote', 'confirm-single');
    if (canCancel) acts.push('cancel');
  } else if (status === 'quoted') {
    if (canCancel) acts.push('cancel');
  } else if (status === 'confirmed') {
    if (opts.isOwner || opts.isApprover || opts.isAssigner) acts.push('mark-completed');
    if (canCancel) acts.push('cancel');
  } else if (status === 'active') {
    acts.push('mark-completed');
  }
  return acts;
}

type Ref = { id: string; type: string };
type EmployeeRef = Ref & { firstName?: string; lastName?: string; email?: string };
type ProjectRef = Ref & { name?: string; code?: string };

export interface TravelBookingRow extends Denormalized {
  type: 'travel-bookings';
  id: string;
  travelType?: string;
  fromDate?: string;
  toDate?: string;
  purpose?: string;
  destination?: string;
  providerName?: string;
  bookingCode?: string;
  externalReference?: string;
  tripReference?: string;
  amount?: number;
  currency?: string;
  payerType?: string;
  priority?: string;
  paidAt?: string;
  statusCategory?: TravelStatus;   // READ-ONLY
  submittedAt?: string; pmApprovedAt?: string; pmRejectedAt?: string; quotedAt?: string;
  confirmedAt?: string; completedAt?: string; cancelledAt?: string;
  rejectionReason?: string; cancellationReason?: string;
  freeFormDetails?: string;
  /* Sorveglianza dell'approvazione (BE v1.92.0): read-only, filtrabili e ordinabili. */
  lastApprovalReminderAt?: string | null;
  approvalReminderCount?: number;
  approvalBypassed?: boolean;
  bypassReason?: string;
  bypassedAt?: string | null;
  /* ── Annullamenti da lavorare (BE v1.99.0) ──
     Quando si annulla una prenotazione **già lavorata** resta qualcosa da disfare: un
     mezzo da liberare, un preventivo da ritirare. Il BE registra da dove veniva e
     traccia chi ha chiuso la pratica. Tutti read-only, tutti filtrabili e ordinabili.

     `statusBeforeCancellation` è il fatto, non un booleano derivato: un campo calcolato
     non sarebbe filtrabile e uno persistito sarebbe una seconda verità da tenere
     allineata. Nasce nullo su tutto lo storico — non è deducibile a posteriori,
     `cancelledAt` non dice da dove — quindi la coda parte vuota e si popola da qui. */
  statusBeforeCancellation?: string | null;
  cancelledByEmployeeId?: string | null;
  cancellationHandledAt?: string | null;
  cancellationHandledByEmployeeId?: string | null;
  cancellationHandlingNotes?: string | null;
  /**
   * Nota interna della segreteria. Dal v1.99.0 è **chiusa anche in lettura**: il BE la
   * toglie dal payload di chi non ha il claim, `?include` compreso, e ha rimosso `filter`
   * e `sort` per tutti (un filtro trovato/non trovato è già una lettura). Non filtrarci
   * né ordinarci: 400.
   */
  internalNotes?: string;
  secretaryNotes?: string;
  createdAt?: string;
  employee?: EmployeeRef | null;
  project?: ProjectRef | null;
  quotes?: Ref[] | null;
}

export interface TravelQuoteRow extends Denormalized {
  type: 'travel-booking-quotes';
  id: string;
  position?: number;
  description?: string;
  providerName?: string;
  amount?: number;
  currency?: string;
  details?: string;
  notes?: string;
  isSelected?: boolean;
  travelBooking?: Ref | null;
}

export interface TravelPolicyRow extends Denormalized {
  type: 'travel-policies';
  id: string;
  name?: string;
  description?: string;
  tier?: string;
  maxHotelPerNight?: number;
  flightClassMax?: string;
  trainClassMax?: string;
  isActive?: boolean;
}

// ─────────────────────────── Workflow ───────────────────────────

const T = 'travel-bookings';
export interface TravelWorkflowAttrs { statusCategory?: TravelStatus; amount?: number; providerName?: string }

export const pmApproveTravel = (id: string, note?: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'pm-approve', note ? { note } : {});
export const pmRejectTravel = (id: string, reason: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'pm-reject', { reason });
export const quoteTravel = (id: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'quote', {});
export const confirmSingleTravel = (id: string, body: { amount?: number; providerName?: string; bookingCode?: string; externalReference?: string }): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'confirm-single', body);
export const cancelTravel = (id: string, reason: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'cancel', { reason });
export const markCompletedTravel = (id: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'mark-completed', {});

/**
 * Sollecita chi deve decidere. `meta.notifiedApprovers` a `0` significa che **non è arrivato
 * a nessuno**: il richiedente non ha un responsabile in gerarchia, oppure i suoi
 * responsabili non hanno un utente collegato. Va detto diversamente da un invio riuscito.
 */
export const remindTravelApprover = (id: string, note?: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'remind-approver', note ? { note } : {});

/**
 * Chiude la pratica di un annullamento (preventivo ritirato, fornitore avvisato). Una
 * seconda marcatura prende 422 con chi ha già chiuso e quando, invece di sovrascrivere in
 * silenzio le note di chi ci ha lavorato prima.
 */
export const markTravelCancellationHandled = (id: string, notes?: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'mark-cancellation-handled', notes ? { notes } : {});

/** Riapre la pratica. */
export const unmarkTravelCancellationHandled = (id: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'unmark-cancellation-handled', {});

/**
 * Trasferte annullate dopo che c'era già qualcosa da disfare, non ancora lavorate.
 * `'active'` è doppiamente morto qui: nessun endpoint porta mai una trasferta a quello
 * stato. Resta per contratto. `null` senza apici, o è 400.
 */
export async function fetchTravelCancellationsToHandle(): Promise<TravelBookingRow[]> {
  return fetchAllPages<TravelBookingRow>(T, {
    filter: {
      statusCategory: { eq: 'cancelled' },
      statusBeforeCancellation: { in: ['quoted', 'confirmed', 'active'] },
      cancellationHandledAt: { eq: null },
    },
    include: ['employee', 'project'],
    sort: ['-cancelledAt'],
  });
}

/** Scavalco tracciato dell'approvazione. Motivo obbligatorio, minimo dieci caratteri. */
export const bypassTravelApproval = (id: string, reason: string): Promise<WorkflowResult<TravelWorkflowAttrs>> =>
  runWorkflowAction<TravelWorkflowAttrs>(T, id, 'bypass-approval', { reason });
/** Seleziona un preventivo → booking a `confirmed`. Endpoint su travel-booking-quotes. */
export const selectTravelQuote = (quoteId: string): Promise<WorkflowResult<{ isSelected?: boolean }>> =>
  runWorkflowAction('travel-booking-quotes', quoteId, 'select', {});

// ─────────────────────────── CRUD ───────────────────────────

export interface CreateTravelInput {
  travelType: string;
  fromDate: string;
  toDate: string;
  destination?: string;
  purpose: string;
  payerType: string;
  priority: string;
  amount?: number;
  currency: string;
  providerName?: string;
  freeFormDetails?: string;
  projectId?: string | null;
}

export async function createTravelBooking(input: CreateTravelInput, extra?: Record<string, unknown>): Promise<{ id: string }> {
  const rel: Record<string, { data: { type: string; id: string } }> = {};
  if (input.projectId) rel.project = { data: { type: 'projects', id: input.projectId } };
  const created = await createOne('travel-bookings', {
    attributes: {
      travelType: input.travelType,
      fromDate: input.fromDate,
      toDate: input.toDate,
      destination: input.destination || null,
      purpose: input.purpose,
      payerType: input.payerType,
      priority: input.priority,
      /* Vuoto = `null`, «non stimato», che non è zero: dal BE v1.272.0 `amount` è annullabile
         (collaudo, punto 2.2). Prima era un decimale obbligatorio, il `null` tornava con un errore
         tecnico e il campo vuoto finiva salvato come 0. */
      amount: input.amount != null && Number.isFinite(input.amount) ? input.amount : null,
      currency: input.currency,
      providerName: input.providerName || null,
      freeFormDetails: input.freeFormDetails || null,
      ...(extra ?? {}),
    },
    relationships: Object.keys(rel).length ? rel : undefined,
  });
  return { id: created?.id ?? '' };
}

/** Aggiorna un booking (per edit pre-conferma / completamento campi type-specific). */
export const updateTravelBooking = (id: string, attributes: Record<string, unknown>): Promise<unknown> =>
  updateOne('travel-bookings', { id, attributes });

/** Campi strutturati per tipo trasferta (BE TravelBooking type-specific). */
export interface TravelTsField { name: string; label: string; kind: 'text' | 'number' | 'switch' | 'datetime' }
export const TRAVEL_TS_FIELDS: Record<string, TravelTsField[]> = {
  hotel: [
    { name: 'hotelName', label: 'Hotel', kind: 'text' },
    { name: 'hotelAddress', label: 'Indirizzo', kind: 'text' },
    { name: 'hotelRoomType', label: 'Tipo camera', kind: 'text' },
    { name: 'breakfastIncluded', label: 'Colazione inclusa', kind: 'switch' },
    { name: 'checkInAt', label: 'Check-in', kind: 'datetime' },
    { name: 'checkOutAt', label: 'Check-out', kind: 'datetime' },
  ],
  train: [
    { name: 'carrierName', label: 'Vettore', kind: 'text' }, { name: 'transportNumber', label: 'N. treno', kind: 'text' },
    { name: 'departureStation', label: 'Stazione partenza', kind: 'text' }, { name: 'arrivalStation', label: 'Stazione arrivo', kind: 'text' },
    { name: 'travelClass', label: 'Classe', kind: 'text' }, { name: 'seatNumber', label: 'Posto', kind: 'text' },
  ],
  flight: [
    { name: 'carrierName', label: 'Compagnia', kind: 'text' }, { name: 'transportNumber', label: 'N. volo', kind: 'text' },
    { name: 'departureAirport', label: 'Aeroporto partenza', kind: 'text' }, { name: 'arrivalAirport', label: 'Aeroporto arrivo', kind: 'text' },
    { name: 'departureTerminal', label: 'Terminal', kind: 'text' }, { name: 'travelClass', label: 'Classe', kind: 'text' }, { name: 'seatNumber', label: 'Posto', kind: 'text' },
  ],
  ferry: [
    { name: 'carrierName', label: 'Compagnia', kind: 'text' }, { name: 'transportNumber', label: 'N. corsa', kind: 'text' },
    { name: 'departurePort', label: 'Porto partenza', kind: 'text' }, { name: 'arrivalPort', label: 'Porto arrivo', kind: 'text' }, { name: 'travelClass', label: 'Classe', kind: 'text' },
  ],
  'car-rental-short': [
    { name: 'rentalCompany', label: 'Autonoleggio', kind: 'text' }, { name: 'pickupLocation', label: 'Ritiro', kind: 'text' },
    { name: 'returnLocation', label: 'Riconsegna', kind: 'text' }, { name: 'carCategory', label: 'Categoria auto', kind: 'text' },
    { name: 'kmIncluded', label: 'Km inclusi', kind: 'number' }, { name: 'fuelPolicy', label: 'Policy carburante', kind: 'text' },
  ],
  transfer: [
    { name: 'transferType', label: 'Tipo transfer', kind: 'text' }, { name: 'location', label: 'Località', kind: 'text' }, { name: 'duration', label: 'Durata', kind: 'text' },
  ],
  parking: [
    { name: 'parkingName', label: 'Parcheggio', kind: 'text' }, { name: 'location', label: 'Località', kind: 'text' }, { name: 'duration', label: 'Durata', kind: 'text' },
  ],
  other: [],
};

export const addTravelQuote = (bookingId: string, attrs: { providerName?: string; amount?: number; currency?: string; description?: string; notes?: string; position?: number }): Promise<{ id: string }> =>
  createOne('travel-booking-quotes', {
    attributes: { ...attrs },
    relationships: { travelBooking: { data: { type: 'travel-bookings', id: bookingId } } },
  }).then((c) => ({ id: c?.id ?? '' }));

export const deleteTravelQuote = (id: string): Promise<void> => deleteOne('travel-booking-quotes', id).then(() => undefined);

export interface TravelLinkRow extends Denormalized {
  type: 'travel-booking-links';
  id: string;
  bookingDate?: string;
  /**
   * Quota attribuita a questa rendicontazione.
   *
   * Dal BE `v1.125.0` il server la **materializza** alla create quando non la passiamo: la riga
   * porta quindi sempre la cifra effettivamente applicata al timesheet. Sui link **storici** può
   * essere `null`, e su quelli lo storno ricade sulla ripartizione calcolata al momento — che nel
   * frattempo può essere cambiata. Vedi `quotaDaMaterializzare()`.
   */
  amountAttributed?: number;
  timesheetEntry?: (Ref & { date?: string; description?: string }) | null;
}

/**
 * Collega una trasferta confermata a una voce di timesheet (il BE compila le spese
 * lodging/transport su quella giornata).
 *
 * ⚠️ `bookingDate` è **obbligatoria**, non opzionale come sembrava: sul BE è una `DateOnly`
 * **non nullable** e fa parte dell'indice UNIQUE `(tenant, trasferta, rendicontazione, data)`.
 * Prima si mandava `bookingDate: null` quando il chiamante non la passava — cioè sempre, perché
 * l'unico chiamante non la passa — e un `null` su una `DateOnly` non nullable non è un default
 * accettato: è un valore che il deserializzatore non sa convertire. La data giusta è quella
 * della **giornata rendicontata**: è il giorno a cui la spesa viene attribuita, ed è ciò che
 * rende l'indice UNIQUE una regola sensata invece di un vincolo su una data assente.
 */
export const addTravelLink = (
  bookingId: string,
  timesheetEntryId: string,
  bookingDate: string,
  attrs?: { amountAttributed?: number },
): Promise<{ id: string }> =>
  createOne('travel-booking-links', {
    // `amountAttributed` resta omissibile: se non la passiamo la calcola il server, ed è la
    // ripartizione in parti uguali fra i link della trasferta — la stessa formula di prima.
    attributes: {
      bookingDate,
      ...(attrs?.amountAttributed != null ? { amountAttributed: attrs.amountAttributed } : {}),
    },
    relationships: {
      travelBooking: { data: { type: 'travel-bookings', id: bookingId } },
      timesheetEntry: { data: { type: 'timesheet-entries', id: timesheetEntryId } },
    },
  }).then((c) => ({ id: c?.id ?? '' }));

/**
 * Cambia la quota di un collegamento esistente (BE §6, `v1.125.0`).
 *
 * Fino alla v1.125.0 la PATCH esisteva ma **non stornava**: cambiare la quota lasciava la spesa
 * sul timesheet al valore vecchio, senza errore. Per questo il FE non aveva un update e l'unico
 * modo di correggere un importo era cancellare e ricreare il legame — due scritture, e nel mezzo
 * una giornata senza spesa. Ora il server fa il diff completo (storna la vecchia coppia
 * rendicontazione/importo e applica la nuova) e tocca il timesheet solo se qualcosa è cambiato
 * davvero, quindi un retry non conta due volte.
 *
 * `timesheetEntryId` sposta il legame su un'altra giornata: passa dallo stesso diff, quindi la
 * spesa lascia la vecchia e arriva sulla nuova in un'unica scrittura.
 */
export const patchTravelLink = (
  id: string,
  input: { amountAttributed?: number | null; bookingDate?: string; timesheetEntryId?: string },
): Promise<void> =>
  updateOne('travel-booking-links', {
    id,
    attributes: {
      ...(input.amountAttributed !== undefined ? { amountAttributed: input.amountAttributed } : {}),
      ...(input.bookingDate !== undefined ? { bookingDate: input.bookingDate } : {}),
    },
    relationships: input.timesheetEntryId
      ? { timesheetEntry: { data: { type: 'timesheet-entries', id: input.timesheetEntryId } } }
      : undefined,
  }).then(() => undefined);

export const deleteTravelLink = (id: string): Promise<void> => deleteOne('travel-booking-links', id).then(() => undefined);

/**
 * La quota che il server calcolerebbe da sé: importo della trasferta diviso i collegamenti.
 *
 * Serve come **suggerimento** nel campo vuoto, non come valore da inviare: mandarla renderebbe
 * nostra una cifra che è del server, e su una trasferta con più collegamenti la nostra e la sua
 * divergerebbero al primo collegamento aggiunto da qualcun altro.
 */
export function splitQuota(bookingAmount?: number | null, linkCount = 0): number | null {
  if (bookingAmount == null) return null;
  return Math.round((bookingAmount / Math.max(1, linkCount)) * 100) / 100;
}

/**
 * I collegamenti **storici** senza quota, cioè quelli creati prima della `v1.125.0`.
 *
 * Su questi lo storno di una modifica o di una cancellazione ricade sulla ripartizione calcolata
 * *in quel momento*: se nel frattempo i collegamenti sono cambiati di numero, la cifra sottratta
 * non è quella che fu aggiunta. È un'imprecisione del BE **preesistente**, non introdotta dal
 * diff — e si chiude scrivendo una quota esplicita, che è esattamente ciò che l'utente può fare
 * ora dal drawer. Per questo si conta: per dirglielo.
 */
export function linkWithoutQuota(links: readonly TravelLinkRow[]): TravelLinkRow[] {
  return links.filter((l) => l.amountAttributed == null);
}

export const refetchTravelBooking = (id: string): Promise<TravelBookingRow | null> =>
  fetchOne<TravelBookingRow>('travel-bookings', id, { include: ['employee', 'project'] });

// ─────────────────────────── Schemi admin (policy) ───────────────────────────

/** `tier` è validato dal BE con regex junior|mid|senior|executive|custom-*, quindi campo testo. */
export const TRAVEL_POLICY_SCHEMA: CrudSchema = {
  type: 'travel-policies', singular: 'policy', defaultSort: ['name'],
  columns: [{ key: 'name', label: 'Nome' }, { key: 'tier', label: 'Fascia' }, { key: 'maxHotelPerNight', label: 'Hotel/notte' }, boolCol('isActive', 'Attiva')],
  fields: [
    { name: 'name', label: 'Nome', kind: 'text', required: true,
      hint: 'Come la policy compare quando si assegna a un dipendente, es. «Trasferte Italia — senior».' },
    { name: 'description', label: 'Descrizione', kind: 'textarea',
      hint: 'A uso di chi configura: cosa copre questa policy e a chi si applica.' },
    { name: 'tier', label: 'Fascia (junior/mid/senior/executive/custom-…)', kind: 'text',
      hint: 'Etichetta della fascia di inquadramento. Non assegna nulla da sola: il collegamento al dipendente si fa nella scheda «Assegnazioni».' },
    { name: 'maxHotelPerNight', label: 'Max hotel/notte', kind: 'decimal',
      hint: 'Tetto di spesa per notte, in euro. Col blocco delle regole di spesa acceso, una trasferta oltre questo tetto non si conferma. Vuoto = nessun tetto.' },
    { name: 'hotelStarClassMax', label: 'Stelle hotel max', kind: 'number',
      hint: 'Categoria massima prenotabile, da 1 a 5. Vuoto = nessun vincolo di categoria.' },
    { name: 'flightClassMax', label: 'Classe volo max', kind: 'text',
      hint: 'Classe più alta ammessa: economy, premium-economy, business o first. Col blocco acceso, una classe sopra questa non si conferma.' },
    { name: 'trainClassMax', label: 'Classe treno max', kind: 'text',
      hint: 'Classe più alta ammessa, con gli stessi valori del volo: economy, premium-economy, business o first. Un altro valore non viene controllato.' },
    { name: 'maxDailyTransport', label: 'Max trasporto/giorno', kind: 'decimal',
      hint: 'Tetto giornaliero per taxi, mezzi pubblici e trasferimenti locali, in euro.' },
    { name: 'requiresQuoteMinAmount', label: 'Preventivo oltre importo', kind: 'decimal',
      hint: 'Oltre questa cifra la trasferta richiede un preventivo prima dell’approvazione. Vuoto = mai richiesto.' },
    { name: 'isActive', label: 'Attiva', kind: 'switch',
      hint: 'Spenta, non è più assegnabile a nuovi dipendenti; le assegnazioni in corso restano valide fino alla loro data di fine.' },
  ],
};

export const TRAVEL_POLICY_TIER_SCHEMA: CrudSchema = {
  type: 'travel-policy-tiers', singular: 'assegnazione fascia', defaultSort: ['-startDate'],
  columns: [refCol('employee', 'Dipendente'), refCol('policy', 'Policy'), dateCol('startDate', 'Dal'), dateCol('endDate', 'Al')],
  fields: [
    { name: 'employee', label: 'Dipendente', kind: 'employee', required: true,
      hint: 'Chi viene assoggettato ai tetti di questa policy.' },
    { name: 'policy', label: 'Policy', kind: 'reference', refType: 'travel-policies', required: true,
      hint: 'Compaiono solo le policy attive.' },
    { name: 'startDate', label: 'Dal', kind: 'date', required: true,
      hint: 'Da quando i tetti valgono. Le trasferte già approvate non vengono ricalcolate.' },
    { name: 'endDate', label: 'Al', kind: 'date',
      hint: 'Vuoto = a tempo indeterminato. Compilalo per un cambio di fascia programmato.' },
    { name: 'notes', label: 'Note', kind: 'textarea',
      hint: 'Perché è stata assegnata questa fascia: resta come traccia per chi controlla i rimborsi.' },
  ],
};

// ─────────────────── Code e registro (BE v1.92.0) ───────────────────

/**
 * Le richieste che **questo** utente può davvero approvare.
 *
 * Il BE la risolve con la stessa primitiva del gate di `/pm-approve`: quello che la coda
 * mostra è esattamente quello che la scrittura accetta. È la proprietà che rende inutile
 * ricostruire la gerarchia lato client — cosa che facevamo con un tetto di 8 livelli e 500
 * dipendenti, e che comunque non vedeva le deleghe diverse da `all`.
 */
export async function fetchTravelPendingForMe(): Promise<TravelBookingRow[]> {
  const r = await httpRequest<TravelBookingRow[]>('/api/travel-bookings/pending-approval-for-me', { endpointType: 'plain' });
  return Array.isArray(r) ? r : [];
}

/**
 * La coda della segreteria. Include le `requested` **solo** se il tenant ha acceso
 * `travel.secretarySeesPendingApproval`: senza quella chiave la coda mostra ciò che
 * mostrava prima, e non è un difetto da diagnosticare.
 *
 * ⚠️ L'endpoint non ha gate di claim: qualunque utente autenticato del tenant la legge.
 * Il BE lo dichiara e ha scelto di non chiuderlo finché i claim non sono su un profilo
 * reale, altrimenti la coda si svuoterebbe per tutti tranne super-admin e Direzione HR.
 */
export async function fetchTravelPendingSecretary(): Promise<TravelBookingRow[]> {
  const r = await httpRequest<TravelBookingRow[]>('/api/travel-bookings/pending-secretary', { endpointType: 'plain' });
  return Array.isArray(r) ? r : [];
}

/**
 * Registro delle trasferte confermate che **intersecano** la finestra — non che ci stanno
 * dentro: una trasferta iniziata prima di `from` e finita dentro compare comunque. Senza
 * `from`/`to` non filtra per data.
 *
 * Nota di contratto: il ramo `active` è corretto ma oggi vuoto — nessun endpoint del
 * workflow trasferte porta mai a quello stato, e il BE lo ha dichiarato.
 */
export async function fetchTravelAssigned(from?: string, to?: string): Promise<TravelBookingRow[]> {
  const p = new URLSearchParams();
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  const qs = p.toString();
  const r = await httpRequest<TravelBookingRow[]>(`/api/travel-bookings/assigned${qs ? `?${qs}` : ''}`, { endpointType: 'plain' });
  return Array.isArray(r) ? r : [];
}
